/**
 * Authenticated Fetch Utility for Magnus Resolve
 * ===========================================
 * Handles Firebase Auth token injection and JSON response parsing.
 * Shared with Magnus-Core ecosystem.
 */

import { getEnv } from './env';
import { auth } from '../config/firebase';

const BACKEND_URL = getEnv('VITE_API_URL') || '';

export async function authenticatedFetchJSON<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    // Get Firebase Auth token
    let token: string | null = null;
    try {
        const user = auth.currentUser;
        if (user) {
            token = await user.getIdToken();
        }
    } catch (error) {
        console.warn('Firebase auth not available for fetch:', error);
    }

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // Handle relative URLs vs absolute URLs
    const isAbsolute = endpoint.startsWith('http');
    const url = isAbsolute ? endpoint : `${BACKEND_URL}${endpoint}`;

    const response = await fetch(url, {
        ...options,
        headers,
    });

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Authentication failed. Token may be invalid or expired.');
        }
        if (response.status === 403) {
            let detail = 'You do not have access to this resource';
            try {
                const errorData = await response.json();
                if (errorData.detail) detail = errorData.detail;
            } catch (e) {
                // Ignore parse error
            }
            throw new Error(`Permission denied: ${detail}`);
        }
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json() as Promise<T>;
}
